import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Col, Row, Container } from 'reactstrap';
import Breadcrumbs from "../../components/Common/Breadcrumb";
import DynamicForm from '../Forms/DynamicForm';
import {
    updateApplication,
    getApplicationWithStatusValidation,
    refreshApplicationStatus
} from '../../helpers/Espo/application_helper';
import {
    getStatusPermissions, APPLICATION_STATUS
} from '../../helpers/statusManager';

const Application = () => {
    const [searchParams] = useSearchParams();
    const applicantId = searchParams.get('applicantId');
    const applicationId = searchParams.get('applicationId');
    const viewOnly = searchParams.get('viewOnly')==='true'?true:false;
    const navigate = useNavigate();

    const [application, setApplication] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusPermissions, setStatusPermissions] = useState({});

    const aidApplicationSchema = require('../Forms/Schema/AidApplication.json');
    // Sort the schema by the 'id' field
    const sortedSchema = aidApplicationSchema.sort((a, b) => a.id - b.id);

    useEffect(() => {
        const loadApplication = async () => {
            try {
                if (applicationId) {
                    const applicationData = await getApplicationWithStatusValidation(applicationId);
                    setApplication(applicationData);
                    
                    // Set status-based permissions
                    const permissions = getStatusPermissions(applicationData.status || APPLICATION_STATUS.DRAFT);
                    setStatusPermissions(permissions);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        loadApplication();
    }, [applicationId]);

    // Add page visibility listener to refresh status when page becomes visible
    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (!document.hidden && applicationId) {
                try {
                    // Refresh application status when page becomes visible
                    const refreshedData = await refreshApplicationStatus(applicationId);
                    setApplication(prev => ({
                        ...prev,
                        ...refreshedData
                    }));
                    
                    // Update permissions based on potentially new status
                    const permissions = getStatusPermissions(refreshedData.status || APPLICATION_STATUS.DRAFT);
                    setStatusPermissions(permissions);
                } catch (error) {
                    console.error('Failed to refresh application status:', error);
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [applicationId]);

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid={true}>
                    <Breadcrumbs title="Moeen Memorial" breadcrumbItem="Application" />
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <DynamicForm
                                    schema={sortedSchema}
                                    initialValues={application}
                                    onSubmit={viewOnly ? undefined : async (formData) => {
                                        try {
                                            // Handle form submission with proper status management
                                            await updateApplication(applicationId, formData, { 
                                                isSubmission: true 
                                            });
                                            
                                            // Show success message
                                            alert('Application submitted successfully!');
                                            
                                            // Navigate to dashboard after successful submission
                                            navigate('/dashboard');
                                        } catch (error) {
                                            console.error('Error submitting application:', error);
                                            alert(`Failed to submit application: ${error.message}`);
                                        }
                                    }}
                                    onSaveDraft={viewOnly ? undefined : async (formData, showAlert = true) => {
                                        try {
                                            // OPTIMIZED: Get latest data first to prevent file ID conflicts
                                            const latestApplication = await getApplicationWithStatusValidation(applicationId);
                                            
                                            // CORRECTED FIX: Always prioritize server file IDs over form data
                                            // Server has the most up-to-date file IDs after uploads
                                            const mergedData = { ...latestApplication }; // Start with server data
                                            
                                            // For non-file fields, use form data to preserve user changes
                                            Object.keys(formData).forEach(key => {
                                                // For file fields: ALWAYS use server values (server is truth after upload)
                                                if (!key.endsWith('Id') && !key.endsWith('Name')) {
                                                    // For non-file fields: use form data to preserve user input
                                                    mergedData[key] = formData[key];
                                                }
                                            });
                                            
                                            const updateResponse = await updateApplication(applicationId, mergedData, { 
                                                isDraft: true 
                                            });
                                            
                                            // OPTIMIZATION: Use the update response data instead of making another GET call
                                            setApplication(updateResponse);
                                            
                                            // Update permissions in case status changed
                                            const permissions = getStatusPermissions(updateResponse.status || APPLICATION_STATUS.DRAFT);
                                            setStatusPermissions(permissions);
                                            
                                            // Show success message only if requested (for manual save draft)
                                            if (showAlert) {
                                                alert('Draft saved successfully!');
                                            }
                                            
                                        } catch (error) {
                                            console.error('Error saving draft:', error);
                                            // Only show error alert if original call requested alerts
                                            if (showAlert) {
                                                alert(`Failed to save draft: ${error.message}`);
                                            }
                                            throw error; // Re-throw to let the form handle the error
                                        }
                                    }}
                                    stepperOn={true}
                                    viewOnly={viewOnly}
                                />
                            </Card>
                            <Button
                                type="button"
                                style={{ float: 'right' }}
                                className="btn btn-primary waves-effect waves-light"
                                color='primary'
                                onClick={() => navigate(-1)}>
                                Back to Dashboard
                            </Button> {/* Go back button */}
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Application;
